import React from 'react'
import propTypes from 'prop-types';
import { FormControl, FormHelperText, IconButton, InputLabel, ListItemText, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { FieldVariants } from './utils/appInputenum';

const SelectField = (props) => {

  //#region props
  const {
    name,
    label,
    value,
    onChange,
    className,
    options,
    variant,
    onBlur,
    required,
    disabled,
  fullWidth,
    error,
    errorMessage,
    helperText,
    readOnly,
    toolTip,
    placeholder
  } = props;

  //#region change events
  const handleChange = (ev) => {
    const { name, value } = ev.target;
    onChange(name, value)
  }

  return (
    <FormControl 
      variant={variant === FieldVariants?.OUTLINED_WITH_LABEL 
                ? FieldVariants.OUTLINED 
                : variant
              } 
      sx={{ maxWidth: '100%' }} 
      fullWidth={fullWidth}>
      {
        FieldVariants.OUTLINED_WITH_LABEL === variant 
        ? <Typography variant="body2" color="textSecondary" marginBottom='10px'>
            <div className='display-flex'>
              <span className=''>
                  {label}
                  {required && <span style={{ color: 'red' }}> *</span>}
              </span>
              {toolTip && (
                  <Tooltip title={toolTip} className='margin-left-10'>
                      <IconButton size="small" style={{ padding: 0 }}>
                          <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                  </Tooltip>
              )}
            </div>
          </Typography>
        : <Tooltip title={toolTip}>
            <InputLabel
              id={name}
              sx={variant === FieldVariants.OUTLINED && (value === "" || options?.length === 0) ? { marginTop: '-8px' } : {}}
              {...(variant !== FieldVariants.OUTLINED && { shrink: true, focused: !disabled })}
              error={error}
              required={required}>
              {label}
            </InputLabel>
          </Tooltip>
      }
      <Select
        fullWidth={fullWidth}
        id={name}
        className={className ? className : ''}
        labelId={name}
        name={name}
        value={options?.some(option => option.value === value) ? value : ''}
        onChange={handleChange}
        error={error}
        sx={{ lineHeight: '1.5em !important' }}
        size={(variant === FieldVariants.OUTLINED || variant === FieldVariants.OUTLINED_WITH_LABEL) 
                ? 'small' 
                : ''
            }
        label={FieldVariants?.OUTLINED_WITH_LABEL === variant 
                ? '' 
                : label}
        renderValue={(selected) => {
          const selectedOption = options?.find(option => option.value === selected);
          const label = selectedOption ? selectedOption.label : '';
          return (
            <Tooltip title={label}>
              <span
                style={{
                  paddingRight: '32px', // Space for dropdown icon
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  maxWidth: 'calc(100% - 32px)', // Prevent text from reaching icon
                  verticalAlign: 'middle'
                }}
              >
                {label}
              </span>
            </Tooltip>
          );
        }}
        onBlur={!readOnly && onBlur}
        disabled={disabled}
        MenuProps={{
          PaperProps: {
            sx: {
              maxWidth: 300, // Set a max width to control dropdown width (adjust as needed)
              overflowY: 'auto', // Enable vertical scrolling for overflowed content
            },
          },
        }}
        inputProps={{ readOnly: readOnly }}>
        {placeholder && (
          <MenuItem value="" disabled hidden>
            <em>{placeholder}</em>
          </MenuItem>
        )}
        {options?.map((item) => (
          <MenuItem
            key={item.value}
            value={item.value}
            disabled={item.isHeader || item.disabled} 
            sx={{
              fontWeight: item.isHeader ? 'bold' : 'normal', 
              pointerEvents: item.isHeader ? 'none' : 'auto', 
              backgroundColor: item.isHeader ? '#f0f0f0' : 'inherit', 
            }}
          >
            <ListItemText
              primary={item.label}
              sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}
            />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText error={error}>
        {error ? errorMessage :
          disabled && helperText ? helperText : ''}
      </FormHelperText>
    </FormControl>
  )
}

SelectField.propTypes = {
  type: propTypes.string,
  onChange: propTypes.func.isRequired,
  variant: propTypes.oneOf(['standard', 'filled', 'outlined']),
  required: propTypes.bool,
  value: propTypes.string,
  placeholder: propTypes.string,
  label: propTypes.string,
  disabled: propTypes.bool,
  options: propTypes.array,
  name: propTypes.string,
  size: propTypes.string,
  errorMessage: propTypes.string,
  helperText: propTypes.string,
  readOnly: propTypes.bool,
  toolTip: propTypes.string,
  placeholder: propTypes.string, 
  fullWidth: propTypes.bool,
};

SelectField.defaultProps = {
  type: 'select',
  variant: 'standard',
  size: 'small',
  required: false,
  value: '',
  placeholder: '',
  label: '',
  options: [],
  disabled: false,
  name: '',
  errorMessage: '',
  helperText: '',
  readOnly: false,
  toolTip: '',
  placeholder: '', 
  fullWidth: true,
};

export default SelectField;